import { useState } from "react";
import { api, day, dateTime } from "../../api.js";
import { useResource } from "../useResource.js";
import { totals, kopUi } from "../../../../lib/pn/calc.js";
import "./pn.css";
import Money from "../../components/Money.jsx";

// Вкладка «Реєстрація» в ПН/РК: накладні, подані через eCabinet (стан, квитанції), і документи в кабінеті ДПС за
// місяць — зокрема створені чи підписані поза сервісом. Кабінет лише читається.
const STATE = {
  creating: ["Створюємо в кабінеті", "idle"], dps_draft: ["Чернетка в кабінеті", "idle"], confirmed: ["Готово до підпису", "idle"],
  signing: ["Підписуємо", "warn"], sending: ["Надсилаємо", "warn"], sent: ["Надіслано, чекаємо квитанцію", "warn"],
  registered: ["Зареєстровано в ЄРПН", "ok"], rejected: ["Відмовлено в реєстрації", "bad"], stopped: ["Реєстрацію зупинено", "bad"],
  unknown: ["Результат невідомий", "bad"], failed: ["Не вдалося", "bad"],
};
// Стан документа в кабінеті (поле flag) — за спостереженнями 2026-10-09: -2 чернетка, 4 підписано, 32 надіслано
const FLAG = { "-2": ["Чернетка, не підписано", "idle"], 4: ["Підписано, не надіслано", "warn"], 32: ["Надіслано", "ok"] };

export default function PnRegistrations({ companyId, reloadKey = 0 }) {
  const [reload, setReload] = useState(0);
  // reloadKey — після синку ПН/РК: він звіряє з ДПС і стани поданих тут накладних
  const reg = useResource(`/api/companies/${companyId}/pn/registrations`, `${reload}:${reloadKey}`);
  const now = new Date();
  const [ym, setYm] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const [y, m] = ym.split("-").map(Number);
  const [showCab, setShowCab] = useState(false);
  const cab = useResource(showCab ? `/api/companies/${companyId}/pn/cabinet-docs?year=${y}&month=${m}` : null, `${ym}:${reload}:${reloadKey}`);
  const [busy, setBusy] = useState(null), [delErr, setDelErr] = useState(null);
  const discard = async (r) => {
    const where = r.dpsDocId ? ` і документ № ${r.dpsDocId} у кабінеті ДПС` : "";
    if (!window.confirm(`Видалити чернетку ПН № ${r.number || "без номера"}${where}? Цю дію не можна скасувати.`)) return;
    setBusy(r.id); setDelErr(null);
    try { await api(`/api/companies/${companyId}/pn/drafts/${r.draftId}/discard`, { method: "POST" }); }
    catch (e) { setDelErr(e.message); }
    setBusy(null); setReload((x) => x + 1);
  };
  const refresh = async (id) => {
    setBusy(id);
    try { await api(`/api/companies/${companyId}/pn/submissions/${id}/refresh`, { method: "POST" }); } catch {}
    setBusy(null); setReload((x) => x + 1);
  };
  const list = reg.data?.registrations || [];
  const cabDocs = cab.data?.docs || [];
  // Однаковий номер у кількох документах кабінету — можливий дубль
  const dupNums = new Set(cabDocs.map((d) => d.number).filter((n, i, a) => n && a.indexOf(n) !== i));

  return (
    <div className="pn-reg">
      <p className="muted small">Накладні, подані через eCabinet. «Зареєстровано» — лише після квитанції ДПС. Ненадіслані чернетки можна видалити — і тут, і в кабінеті.</p>
      {delErr && <p className="error" role="alert">{delErr}</p>}
      {reg.error ? <p className="error">{reg.error.message}</p> : reg.pending ? <p className="muted">Завантаження…</p> : !list.length ? (
        <p className="muted">Ще немає поданих накладних.</p>
      ) : (
        <div className="table-wrap">
          <table>
            <thead><tr><th>Дата</th><th>№</th><th>Кому</th><th className="num">Разом з ПДВ</th><th>Стан</th><th>Документ у кабінеті</th><th><span className="sr-only">Дії</span></th></tr></thead>
            <tbody>
              {list.map((r) => {
                const [label, tone] = STATE[r.state] || [r.state, "idle"];
                const t = totals(r.docRows || [], r.priceMode);
                return (
                  <tr key={r.id}>
                    <td>{day(r.date)}</td><td>{r.number}</td>
                    <td>{r.kind === "nonpayer" ? "Неплатник" : "Платник ПДВ"}</td>
                    <td className="num"><Money text={kopUi(t.total)} /></td>
                    <td><span className={`badge badge-${tone}`}>{label}</span>{r.reason && ["failed", "unknown"].includes(r.state) && <div className="muted small">{r.reason.slice(0, 120)}</div>}</td>
                    <td className="muted small">{r.dpsDocId || "—"}{r.sentAt ? ` · надіслано ${dateTime(r.sentAt)}` : ""}</td>
                    <td className="actions">
                      <a className="btn btn-ghost btn-sm" href={`#/c/${companyId}/pn/${r.draftId}`}>Відкрити</a>
                      {["creating", "dps_draft", "confirmed", "failed"].includes(r.state) && (
                        <button type="button" className="btn btn-ghost btn-sm pn-del-btn" disabled={busy === r.id} onClick={() => discard(r)}>{busy === r.id ? "…" : "Видалити"}</button>
                      )}
                      {r.dpsDocId && ["sent", "unknown", "sending"].includes(r.state) && (
                        <button type="button" className="btn btn-secondary btn-sm" disabled={busy === r.id} onClick={() => refresh(r.id)}>{busy === r.id ? "…" : "Перевірити"}</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <details className="pn-cab" onToggle={(e) => setShowCab(e.currentTarget.open)}>
        <summary><b>Документи в кабінеті ДПС</b> <span className="muted small">чернетки, підписані й надіслані — зокрема створені поза eCabinet</span></summary>
        <div className="row" style={{ marginTop: 10 }}>
          <label className="field field-inline">Місяць<input type="month" value={ym} onChange={(e) => setYm(e.target.value)} /></label>
        </div>
        {cab.error ? <p className="error">{cab.error.message}</p> : cab.pending ? <p className="muted">Читаємо кабінет…</p> : !cabDocs.length ? <p className="muted">Документів за цей місяць немає.</p> : (
          <>
            {dupNums.size > 0 && <p className="error" role="alert">Номер {[...dupNums].join(", ")} мають кілька документів у кабінеті. Не надсилайте зайвий — буде дубль ПН.</p>}
            <div className="table-wrap">
              <table>
                <thead><tr><th>Документ</th><th>Форма</th><th>№</th><th>ІПН покупця</th><th>Стан у кабінеті</th><th>Квитанції</th><th>Змінено</th><th>Звідки</th></tr></thead>
                <tbody>
                  {cabDocs.map((d) => {
                    const [label, tone] = FLAG[d.flag] || [`стан ${d.flag}`, "idle"];
                    return (
                      <tr key={d.id} className={dupNums.has(d.number) ? "pn-dup" : ""}>
                        <td className="mono-sm">{d.id}</td><td>{d.cdoc}</td><td>{d.number || "—"}</td><td>{d.buyerIpn || "—"}</td>
                        <td><span className={`badge badge-${tone}`}>{label}</span>{d.kvtError === -1 && <span className="badge badge-bad">відмова</span>}</td>
                        <td>{d.kvt || 0}</td><td className="muted small">{d.operDate}</td>
                        <td className="muted small">{d.ours ? "eCabinet" : "поза eCabinet"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </details>
    </div>
  );
}
